import { useState, useEffect, useCallback, useRef } from "react";

interface UseTabletIdleTimerProps {
  timeout: number; // in seconds
  onIdle: () => void;
  enabled?: boolean;
}

export function useTabletIdleTimer({
  timeout,
  onIdle,
  enabled = true,
}: UseTabletIdleTimerProps) {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setIsIdle(false);

    timerRef.current = setTimeout(() => {
      setIsIdle(true);
      onIdle();
    }, timeout * 1000);
  }, [timeout, onIdle, enabled]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      return;
    }

    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "touchmove",
      "click",
    ];

    // Start the timer
    resetTimer();

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer, enabled]);

  const wakeUp = useCallback(() => {
    setIsIdle(false);
    resetTimer();
  }, [resetTimer]);

  return {
    isIdle,
    resetTimer,
    wakeUp,
  };
}
